import { useEffect, useRef, useCallback } from "react";
import { useRoom } from "@liveblocks/react/suspense";
import { useVariables } from "../context/VariablesContext";
import { useVariablesApi } from "./useVariablesApi";
import { sanitizeVariableName } from "../utils";
import type { PlaceholderConfig } from "pages/flyer/builder/drawer/types";

/**
 * Hook that persists placeholder configuration to Liveblocks room storage.
 * Saves when selections change, restores when the design loads.
 * Works across browsers/sessions because Liveblocks syncs to its cloud.
 */
export const usePlaceholderPersistence = () => {
  const room = useRoom();
  const {
    selectedDefaultPlaceholders,
    setSelectedDefaultPlaceholders,
    selectedCustomVariables,
    setSelectedCustomVariables,
    selectedExistingPlaceholders,
    setSelectedExistingPlaceholders,
    selectedAudiencePlaceholders,
    setSelectedAudiencePlaceholders,
    customPlaceholders,
    setCustomPlaceholders,
    selectedVariableFile,
    setSelectedVariableFile,
    existingVariableFiles,
    setExistingFileData,
  } = useVariables();

  const { fetchVariables, downloadAndParseCSV } = useVariablesApi();
  const isRestoredRef = useRef(false);
  const prevConfigRef = useRef<string>("");

  // RESTORE: on mount, read placeholderConfig from Liveblocks storage
  useEffect(() => {
    if (isRestoredRef.current) return;

    const restore = async () => {
      try {
        const storage = await room.getStorage();
        const root = storage.root;
        const savedConfigStr = root.get("placeholderConfig") as string | undefined;

        if (savedConfigStr) {
          const config: PlaceholderConfig = JSON.parse(savedConfigStr);

          // Restore non-default selections first
          if (config.selectedCustomVariables?.length) {
            setSelectedCustomVariables(config.selectedCustomVariables);
          }
          if (config.selectedExistingPlaceholders?.length) {
            setSelectedExistingPlaceholders(config.selectedExistingPlaceholders);
          }
          if (config.selectedAudiencePlaceholders?.length) {
            setSelectedAudiencePlaceholders(config.selectedAudiencePlaceholders);
          }
          // Restore defaults, excluding any that overlap with non-default selections
          if (config.selectedDefaultPlaceholders?.length) {
            const nonDefaultSanitized = new Set(
              [
                ...(config.selectedCustomVariables || []),
                ...(config.selectedExistingPlaceholders || []),
                ...(config.selectedAudiencePlaceholders || []),
              ].map((n: string) => sanitizeVariableName(n))
            );
            const cleanedDefaults = config.selectedDefaultPlaceholders.filter(
              (p: string) => !nonDefaultSanitized.has(sanitizeVariableName(p))
            );
            setSelectedDefaultPlaceholders(cleanedDefaults);
          }
          if (config.customPlaceholders?.length) {
            setCustomPlaceholders(config.customPlaceholders);
          }
          prevConfigRef.current = savedConfigStr;

          // If there's a saved variable file, fetch the files list and download CSV for preview
          if (config.selectedVariableFileId) {
            fetchVariables().then((files) => {
              if (files.length > 0) {
                const file = files.find((f) => f.id === config.selectedVariableFileId);
                if (file) {
                  setSelectedVariableFile(file);
                  // Also download the CSV data so preview works without re-selecting
                  if (file.csvUrl) {
                    downloadAndParseCSV(file.csvUrl).then((data) => {
                      if (data) {
                        setExistingFileData(data);
                      }
                    });
                  }
                }
              }
            });
          }
        }
      } catch (e) {
        console.error("[PlaceholderPersistence] Restore failed:", e);
      }
      isRestoredRef.current = true;
    };

    restore();
  }, [room]);

  // Deferred restore of selectedVariableFile once existingVariableFiles loads
  // Skip if user already interacted with file selection (prevents re-restoring after "None")
  const userChangedFileRef = useRef(false);
  const prevSelectedFileRef = useRef(selectedVariableFile);
  useEffect(() => {
    // Track if user changed the file selection after initial restore
    if (isRestoredRef.current && prevSelectedFileRef.current !== selectedVariableFile) {
      userChangedFileRef.current = true;
    }
    prevSelectedFileRef.current = selectedVariableFile;
  }, [selectedVariableFile]);

  useEffect(() => {
    if (
      !isRestoredRef.current ||
      !prevConfigRef.current ||
      existingVariableFiles.length === 0 ||
      selectedVariableFile ||
      userChangedFileRef.current
    )
      return;
    try {
      const config: PlaceholderConfig = JSON.parse(prevConfigRef.current);
      if (config.selectedVariableFileId) {
        const file = existingVariableFiles.find((f) => f.id === config.selectedVariableFileId);
        if (file) {
          setSelectedVariableFile(file);
        }
      }
    } catch {
      // ignore
    }
  }, [existingVariableFiles, selectedVariableFile, setSelectedVariableFile]);

  // SAVE: when selections change, write to Liveblocks storage
  const saveConfig = useCallback(async () => {
    const config: PlaceholderConfig = {
      selectedVariableFileId: selectedVariableFile?.id,
      selectedDefaultPlaceholders,
      selectedCustomVariables,
      selectedExistingPlaceholders,
      selectedAudiencePlaceholders,
      customPlaceholders,
    };
    const configStr = JSON.stringify(config);

    // Skip if unchanged or not yet restored (to avoid overwriting with empty state)
    if (!isRestoredRef.current || configStr === prevConfigRef.current) return;
    prevConfigRef.current = configStr;

    try {
      const storage = await room.getStorage();
      storage.root.set("placeholderConfig", configStr);
    } catch (e) {
      console.error("[PlaceholderPersistence] Save failed:", e);
    }
  }, [
    room,
    selectedVariableFile,
    selectedDefaultPlaceholders,
    selectedCustomVariables,
    selectedExistingPlaceholders,
    selectedAudiencePlaceholders,
    customPlaceholders,
  ]);

  useEffect(() => {
    saveConfig();
  }, [saveConfig]);
};
